// ============================================================================
// TrackRecordCard — admin-only signal performance widget.
//
// Reads from /api/signals/performance — net-of-cost win-rate, Wilson 95% CI,
// expectancy, profit factor, per-direction split, all bucketed by trailing
// window. Pinned windows on the server include 15 / 30 / 90 / 180 days so
// the timeframe buttons resolve instantly off the by_window array (no
// per-click refetch needed).
//
// Used to live on the Dashboard's Trust band; moved into the AdminPage
// Signal Engine tab per product direction so end users see only their own
// portfolio there, not aggregate signal performance.
// ============================================================================

import { useEffect, useState } from 'react';
import { apiGetSignalPerformance } from '../utils/backendApi';
import Icon from './ui/Icon';
import DirBadge from './ui/DirBadge';

const WINDOWS = [15, 30, 90, 180];

export default function TrackRecordCard() {
  const [data, setData] = useState(null);
  const [activeDays, setActiveDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiGetSignalPerformance(activeDays)
      .then(p => { if (!cancelled) setData(p); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeDays]);

  const perf = (data?.by_window || []).find(w => w.days === activeDays) || data;

  if (loading && !perf) {
    return (
      <div className="card" style={{ padding: 'var(--card-pad)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <span style={{
            width: 26, height: 26, borderRadius: 'var(--r-sm)',
            background: 'var(--accent-dim)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}><Icon name="target" size={14} color="var(--accent-soft)" /></span>
          <span style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>Signal Track Record</span>
        </div>
        <div className="skeleton" style={{ height: 14, width: '85%', borderRadius: 4, marginBottom: 8 }} />
        <div className="skeleton" style={{ height: 14, width: '55%', borderRadius: 4 }} />
      </div>
    );
  }

  if (!perf || perf.scored === 0) {
    return (
      <div className="card" style={{ padding: 'var(--card-pad)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <span style={{
            width: 26, height: 26, borderRadius: 'var(--r-sm)',
            background: 'var(--accent-dim)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}><Icon name="target" size={14} color="var(--accent-soft)" /></span>
          <span style={{ fontSize: 13, fontWeight: 700 }}>Signal Track Record</span>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          A {activeDays}-day net-of-cost win rate will appear here once the
          first signals have run their course (≈5 trading days from the first
          digest).
        </div>
      </div>
    );
  }

  const wr      = perf.win_rate_pct;
  const wrCol   = wr >= 55 ? 'var(--green)' : wr >= 45 ? 'var(--amber)' : 'var(--red)';
  const ret     = perf.avg_return_pct;
  const retCol  = ret >= 0 ? 'var(--green)' : 'var(--red)';

  return (
    <div className="card" style={{ padding: 'var(--card-pad)', minWidth: 0 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 14, gap: 8, flexWrap: 'wrap',
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            width: 26, height: 26, borderRadius: 'var(--r-sm)',
            background: 'var(--accent-dim)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}><Icon name="target" size={14} color="var(--accent-soft)" /></span>
          <span style={{ fontSize: 13, fontWeight: 700 }}>Signal Track Record</span>
        </span>
        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {WINDOWS.map((d) => {
            const on = d === activeDays;
            return (
              <button key={d} onClick={() => setActiveDays(d)} style={{
                fontSize: 10, fontWeight: 700, padding: '4px 9px',
                borderRadius: 'var(--r-sm)', cursor: 'pointer',
                border: on ? '1px solid var(--border-strong)' : '1px solid var(--border-subtle)',
                background: on ? 'var(--accent-dim)' : 'transparent',
                color:      on ? 'var(--accent-soft)' : 'var(--text-muted)',
                fontFamily: 'inherit',
              }}>{d}d</button>
            );
          })}
        </div>
      </div>

      <p style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text-secondary)', marginBottom: 16 }}>
        Over the last {activeDays} days, signals closed in profit{' '}
        <b style={{ color: wrCol }}>{wr.toFixed(0)}% of the time</b>, netting{' '}
        <b style={{ color: retCol }}>{ret >= 0 ? '+' : ''}{ret.toFixed(2)}%</b> on average after costs.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
        {[
          { l: 'Win rate',   v: `${wr.toFixed(0)}%`,                         c: wrCol,                                                                 sub: perf.win_rate_ci_low != null ? `95% CI ${perf.win_rate_ci_low.toFixed(0)}–${perf.win_rate_ci_high.toFixed(0)}%` : '' },
          { l: 'Avg return', v: `${ret >= 0 ? '+' : ''}${ret.toFixed(2)}%`,  c: retCol,                                                                sub: perf.avg_gross_return_pct != null ? `gross ${perf.avg_gross_return_pct >= 0 ? '+' : ''}${perf.avg_gross_return_pct.toFixed(2)}%` : '' },
          { l: 'Expectancy', v: `${(perf.expectancy_pct ?? 0) >= 0 ? '+' : ''}${(perf.expectancy_pct ?? 0).toFixed(2)}%`, c: (perf.expectancy_pct ?? 0) >= 0 ? 'var(--green)' : 'var(--red)', sub: perf.profit_factor != null ? `PF ${perf.profit_factor.toFixed(2)}` : '' },
          { l: 'Scored',     v: String(perf.scored),                          c: 'var(--text-primary)',                                                 sub: `${perf.wins}W · ${perf.losses}L` },
        ].map((m) => (
          <div key={m.l}>
            <div className="eyebrow" style={{ marginBottom: 5 }}>{m.l}</div>
            <div className="mono" style={{ fontSize: 21, fontWeight: 800, color: m.c, lineHeight: 1 }}>{m.v}</div>
            {m.sub && <div className="mono" style={{ fontSize: 9.5, color: 'var(--text-muted)', marginTop: 4 }}>{m.sub}</div>}
          </div>
        ))}
      </div>

      {perf.by_direction?.length > 1 && (
        <div style={{
          display: 'flex', gap: 16, marginTop: 16, paddingTop: 14,
          borderTop: '1px solid var(--border-subtle)', flexWrap: 'wrap',
        }}>
          {perf.by_direction.map((d) => (
            <div key={d.direction} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <DirBadge dir={d.direction} size="sm" />
              <span className="mono" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {d.win_rate_pct.toFixed(0)}% · {d.avg_return_pct >= 0 ? '+' : ''}{d.avg_return_pct.toFixed(2)}%
              </span>
            </div>
          ))}
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 10.5, color: 'var(--text-muted)', alignSelf: 'center' }}>
            net of 15 bps cost
          </span>
        </div>
      )}
    </div>
  );
}
